import { useState } from 'react';
import useSWR from 'swr';
import { useScope } from '@workspace/scope-context';
import { SalesLeadsWorkspace, type SalesLeadRow } from '@workspace/silk-ui';

interface LeadPage { items: SalesLeadRow[]; total: number }

async function loadLeads([url]: readonly [string, string]): Promise<LeadPage> {
  const response = await fetch(url, { credentials: 'include', cache: 'no-store' });
  if (!response.ok) throw new Error(response.status === 403 ? 'You do not have access to leads for this tenant.' : 'Could not load leads. Please try again.');
  return response.json();
}

function TenantLeads({ tenantID }: { tenantID: string }) {
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const query = new URLSearchParams({ search, limit: String(pagination.pageSize), offset: String(pagination.pageIndex * pagination.pageSize) });
  const { data, error, isLoading, mutate } = useSWR([`/api/v1/sales/leads?${query}`, tenantID] as const, loadLeads, { keepPreviousData: false, shouldRetryOnError: false, revalidateOnFocus: false });
  return <SalesLeadsWorkspace leads={error ? [] : data?.items ?? []} total={error ? 0 : data?.total ?? 0} loading={isLoading} error={error?.message} search={search}
    onSearchChange={(value) => { setSearch(value); setPagination(current => ({ ...current, pageIndex: 0 })); }}
    pagination={pagination} onPaginationChange={setPagination} onRetry={() => { void mutate(); }} />;
}

export default function SalesLeadsPage() {
  const { activeTenant } = useScope();
  if (!activeTenant?.id) return <p className="p-6 text-sm text-muted-foreground">Select a tenant to view leads.</p>;
  return <TenantLeads key={activeTenant.id} tenantID={activeTenant.id} />;
}
